import { useLingui } from '@lingui/react/macro';
import { SearchX } from 'lucide-react';
import type { QueryKey } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import type { z } from 'zod';

import { useIsMobile } from '@/shared/mobile';
import {
  BlockRenderer,
  EmptyState,
  useBlockData,
  type Block,
  type BlockData,
  type SourceMap,
} from '@/shared/page';
import type { AnyScreenAction } from '@/shared/screen';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';

import { ActionRunnerProvider, type ActionSpec } from './actions';
import { PageHeader } from './page-header';
import { ScreenBlockHost } from './screen-host';

export interface ListScreenProps<PS extends z.ZodType, S extends SourceMap> {
  readonly title: ReactNode;
  readonly eyebrow?: ReactNode;
  /** Contexto do cabeçalho; recebe o dado do bloco para mostrar a contagem ("23 itens"). */
  readonly description?: (data: BlockData<S> | undefined) => ReactNode;
  /** Ações do cabeçalho (manifesto): atualizar, cadastrar… */
  readonly actions?: readonly ActionSpec[];
  readonly refreshAction?: boolean;
  /** Ações que o bloco pode pedir ao hospedeiro (as do manifesto; `ActionSpec` para Desfazer, verbo…). */
  readonly blockActions: readonly (AnyScreenAction | ActionSpec)[];
  /** Chaves invalidadas depois de uma ação com efeito. */
  readonly invalidate?: readonly QueryKey[];
  /** Barra de filtros da tela (ela produz os `params`, 09 §10.3). O `FilterBar` completo é da onda 1. */
  readonly filters?: ReactNode;
  /** O bloco da lista e os seus params, já traduzidos da URL pelo hospedeiro. */
  readonly block: Block<PS, S>;
  readonly params: z.input<PS>;
  /** Há filtro ativo: o vazio vira "nenhum resultado com esses filtros" + Limpar filtros (U39 §9). */
  readonly filtered?: boolean;
  readonly onClearFilters?: () => void;
  /** Painel ao lado da lista a partir de 1280 px (U39 §4), abaixo dela antes disso. */
  readonly aside?: ReactNode;
  readonly className?: string;
}

/**
 * Arquétipo Lista (U39 §1): `PageHeader` → barra de filtros → bloco da lista. O estado mora na URL
 * (`listSearchSchema` + `toListParams`); a tela só traduz a URL em `params` e executa as intenções
 * do bloco pelo manifesto.
 */
export function ListScreen<PS extends z.ZodType, S extends SourceMap>(
  props: ListScreenProps<PS, S>,
) {
  return (
    <ActionRunnerProvider {...(props.invalidate ? { invalidate: props.invalidate } : {})}>
      <ScreenBlockHost actions={props.blockActions}>
        <ListScreenContent {...props} />
      </ScreenBlockHost>
    </ActionRunnerProvider>
  );
}

function ListScreenContent<PS extends z.ZodType, S extends SourceMap>({
  title,
  eyebrow,
  description,
  actions,
  refreshAction,
  filters,
  block,
  params,
  filtered = false,
  onClearFilters,
  aside,
  className,
}: ListScreenProps<PS, S>) {
  const { t } = useLingui();
  const isMobile = useIsMobile();
  // Mesma chave de cache do bloco: não duplica chamada, só lê a contagem para o cabeçalho.
  const result = useBlockData(block, params);
  const empty =
    filtered && onClearFilters ? (
      <EmptyState
        icon={SearchX}
        title={t`Nenhum resultado com esses filtros`}
        description={t`Confira os filtros ou limpe para ver tudo.`}
        compact={isMobile}
        action={
          <Button variant="secondary" size={isMobile ? 'lg' : 'default'} onClick={onClearFilters}>
            {t`Limpar filtros`}
          </Button>
        }
      />
    ) : undefined;

  return (
    <div data-slot="list-screen" className={cn('flex min-w-0 flex-col gap-5', className)}>
      <PageHeader
        {...(refreshAction
          ? { refreshAction: { onRefresh: result.refetch, isRefreshing: result.isFetching } }
          : {})}
        title={title}
        eyebrow={eyebrow}
        description={description?.(result.data)}
        {...(actions ? { actions } : {})}
      />
      {filters ? (
        <div role="search" aria-label={t`Filtros`} className="flex flex-wrap items-center gap-2">
          {filters}
        </div>
      ) : null}
      <div
        className={cn(
          'grid min-w-0 gap-5',
          aside ? 'min-[1280px]:grid-cols-[minmax(0,1fr)_380px] min-[1280px]:items-start' : null,
        )}
      >
        <BlockRenderer block={block} params={params} {...(empty ? { empty } : {})} />
        {aside}
      </div>
    </div>
  );
}
